import packageJson from "../../package.json";
import { EnvironmentEnum } from "@/constants/constants";
// Types
interface LogData {
  message: string;
  level: string;
  error: {
    name: string;
    message: string;
    stack?: string;
  };
  context: Record<string, any>;
  timestamp: string;
}

interface MetricData {
  series: Array<{
    metric: string;
    points: number[][];
    tags: string[];
    type: string;
  }>;
}
const getEnvironment = (): EnvironmentEnum => {
  if (process.env.NODE_ENV === "development") {
    return EnvironmentEnum.DEVELOPMENT;
  }

  if (process.env.NEXT_PUBLIC_IS_PRODUCTION === "true") {
    return EnvironmentEnum.PRODUCTION;
  }
  return EnvironmentEnum.DEVELOPMENT;
};
// Configuration
const CONFIG = {
  env: getEnvironment(), // Reverted to match your expected environment,
  version: packageJson.version,
  site: "datadoghq.com",
  serverService: "stage-web",
  logsEndpoint: (apiKey: string) => {
    const site = "datadoghq.com";
    return `https://http-intake.logs.${site}/v1/input/${apiKey}`;
  },
  metricsEndpoint: (() => {
    const site = "datadoghq.com";
    return `https://api.${site}/api/v1/series`;
  })(),
};

// Utility functions
const formatTags = (tags: Record<string, string>): string[] =>
  Object.entries(tags).map(([key, val]) => `${key}:${val}`);

const createTimestamp = (): number => Math.floor(Date.now() / 1000);

// Server-side Datadog API client
class DatadogAPIClient {
  private readonly apiKey: string | undefined;
  private readonly appKey: string | undefined;
  private readonly logsURL: string;
  private readonly metricsURL: string;

  constructor() {
    this.apiKey = process.env.NEXT_PUBLIC_DD_API_KEY;
    this.appKey = process.env.NEXT_PUBLIC_DD_APP_KEY;
    this.logsURL = CONFIG.logsEndpoint(this.apiKey || "");
    this.metricsURL = CONFIG.metricsEndpoint;
  }

  private async makeRequest(
    url: string,
    data: any,
    headers: Record<string, string>,
  ): Promise<void> {
    if (!this.apiKey) return;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error(
          `Datadog API error: ${response.status} ${response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Error in Datadog API request:", error);
    }
  }

  async sendLog(logData: LogData): Promise<void> {
    const payload = {
      ddsource: "nodejs",
      ddtags: `env:${CONFIG.env},service:${CONFIG.serverService},version:${CONFIG.version}`,
      hostname: process.env.HOSTNAME || process.env.VERCEL_URL || "localhost",
      service: CONFIG.serverService,
      status: "error",
      ...logData,
    };

    await this.makeRequest(this.logsURL, payload, {});
  }

  async sendMetric(
    metricName: string,
    value: number,
    tags: Record<string, string> = {},
  ): Promise<void> {
    const metricData: MetricData = {
      series: [
        {
          metric: metricName,
          points: [[createTimestamp(), value]],
          tags: formatTags(tags),
          type: "count",
        },
      ],
    };

    await this.makeRequest(this.metricsURL, metricData, {
      "DD-API-KEY": this.apiKey!,
    });
  }
}

// Server-side Datadog tracer
class DatadogTracer {
  private currentSpan: Record<string, any> | null = null;

  scope() {
    return { active: () => this.currentSpan };
  }

  startSpan(operation: string) {
    return {
      setTag: (key: string, value: any) => {
        if (!this.currentSpan) this.currentSpan = {};
        this.currentSpan[key] = value;
      },
      finish: () => {
        this.currentSpan = null;
      },
    };
  }

  get dataDogStats() {
    return {
      increment: (
        name: string,
        value: number,
        tags: Record<string, string> = {},
      ) => {
        datadogClient.sendMetric(name, value, tags);
      },
    };
  }
}

// Initialize instances
const datadogClient = new DatadogAPIClient();
const datadogTracer = new DatadogTracer();

// Server-side error logging function
export const logServerError = async (
  error: unknown | any,
  context: Record<string, any> = {},
): Promise<void> => {
  return;
};

export default datadogTracer;
